'use client'

import InputField, { InputFieldProps } from '@trustoo/components/input/InputField'
import { useSearchPickerContext } from '@trustoo/components/searchPicker/SearchPickerContext'
import clsx from 'clsx'
import { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useEffect, useRef } from 'react'

export interface SearchPickerInputProps extends Omit<InputFieldProps, 'value'> {}

/**
 * SearchPicker.Input - User Input Component
 *
 * **Role in Compound Structure:**
 * This component renders the text input field where users type their search query.
 * It synchronizes user input with the picker's state and handles input-specific
 * interactions like opening the dropdown and triggering reset.
 *
 * **Responsibilities:**
 * - Renders the search input field (using InputField component)
 * - Synchronizes input value with picker's query state
 * - Opens dropdown when user types or focuses
 * - Triggers onReset when input is cleared and a selection exists
 * - Handles keyboard interactions (Escape to close)
 * - Applies inputFieldHeight from settings to input label
 * - Forwards all standard input props (onChange, onFocus, etc.)
 */
const SearchPickerInput = ({
    onChange,
    onClick,
    onKeyDown,
    labelClassName,
    labelStyle,
    error,
    ...restInputFieldProps
}: SearchPickerInputProps) => {
    const {
        query,
        search,
        isOpen,
        setIsOpen,
        closePicker,
        selectedOption,
        onResetOption,
        settings,
    } = useSearchPickerContext()
    const inputRef = useRef<HTMLInputElement>(null)

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const newQuery = e.target.value
            search(newQuery)

            // If input is cleared and a selected option exists, call onReset
            if (newQuery.length === 0 && selectedOption && onResetOption) {
                onResetOption()
            }

            if (!isOpen && newQuery.length > 0) {
                setIsOpen(true)
            }
            // Call original onChange
            onChange?.(e)
        },
        [isOpen, search, setIsOpen, onChange, selectedOption, onResetOption],
    )

    const handleClick = useCallback(
        (e: MouseEvent<HTMLInputElement>) => {
            setIsOpen(true)
            // Call original onClick
            onClick?.(e)
        },
        [setIsOpen, onClick],
    )

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Escape') {
                e.preventDefault()
                e.stopPropagation()
                closePicker()
                inputRef.current?.blur()
            }
            // Call original onKeyDown
            onKeyDown?.(e)
        },
        [closePicker, onKeyDown, inputRef],
    )

    // When the dropdown is opened via the controlled isOpen prop and the input is not focused,
    // focus it so the user can type immediately.
    useEffect(() => {
        if (!isOpen) return
        const id = requestAnimationFrame(() => {
            const input = inputRef.current
            if (input && document.activeElement !== input) {
                input.focus()
            }
        })
        return () => cancelAnimationFrame(id)
    }, [isOpen])

    return (
        <InputField
            {...restInputFieldProps}
            ref={inputRef}
            value={query}
            error={error}
            onChange={handleChange}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            labelClassName={clsx(isOpen && !error && 'border-none', labelClassName)}
            labelStyle={{
                ...labelStyle,
                height: settings.inputFieldHeight,
            }}
        />
    )
}

export default SearchPickerInput
